// Free resources' view model (FreeResources.dc.html), from the catalogue rows the member can read.
// Pure: no network, no React, and nothing but format.js, so a test can import it as it is.
//
// - The cards: every live free item, linked or a resource pack, in the catalogue's order, each
//   from its own row and settings.card. A hold takes an item away through row level security, so
//   the page draws only what comes back. Nothing about an item is written here.
// - What each card does follows from its row: a link out (settings.link_url), a 1-click join
//   (settings.join) or a file to download (a resource pack's file). A free pack of 2 files or more
//   (the product-types plan, 13 Oct) lists each file under its blurb, each with its own Download; a
//   pack of 1, the 21 Ways guide's, is the board's card, its button downloading the file.
// - The strip under the cards says how each item the member can see opens, and nothing else.
import * as f from '../lib/format.js'

const LOOK = { master: 'master', accelerator: 'acc', funding_engine: 'fe', poyt: 'poyt', own: 'master' }

// The art panel above each card: the item's look and tone (FreeResources.dc.html draws acc-light,
// master-light and acc-solid)
export function artKey(p) {
  const card = p.settings?.card || {}
  return `${LOOK[p.look] || 'master'}-${card.tone === 'solid' ? 'solid' : 'light'}`
}

// "On ScoreApp. Under 4 minutes." -> "ScoreApp"
function platformOf(note) {
  const m = /^On ([^.]+)\./.exec(note || '')
  return m ? m[1] : null
}

// What the card's button does
function actionFor(p, d) {
  const card = p.settings?.card || {}
  const label = card.button_label || 'Open'
  const note = card.button_note || ''
  if (p.settings?.join) return { kind: 'join', label, note }
  if (p.settings?.link_url) return { kind: 'link', label, note, url: p.settings.link_url }
  if (p.type === 'resource_pack') {
    // a pack's files, in its own order; the Free resources board draws a pack of 1 file
    const files = packFiles(p, d)
    if (files.length) return { kind: 'download', label, note, placementId: files[0].placementId, files: files.length > 1 ? files : null }
  }
  return { kind: null, label, note }
}

// A free pack's files, in its own order: { placementId, title }
export function packFiles(p, d) {
  return (d.placements || [])
    .filter(pl => pl.product_id === p.id && !pl.removed_at)
    .map(pl => ({ pl, item: (d.items || []).find(i => i.id === pl.item_id) }))
    .filter(x => x.item?.kind === 'file')
    .sort((a, b) => (a.pl.sort_order ?? 0) - (b.pl.sort_order ?? 0))
    .map(x => ({ placementId: x.pl.id, title: x.pl.title_override || x.item.title || '' }))
}

// The strip's sentence for 1 item: its own (settings.card.info_line) if it has one, or what its
// button does
export function infoLine(item) {
  if (item.infoLine) return item.infoLine
  const what = `The ${item.kindLabel.toLowerCase() || 'item'}`
  switch (item.action.kind) {
    case 'link': {
      const where = platformOf(item.action.note)
      return where ? `${what} opens on ${where}.` : `${what} opens in a new tab.`
    }
    case 'download': return `${what} downloads from here.`
    case 'join': return `${what} starts with 1 click, using the name and email on your account, and runs by email from there.`
    default: return ''
  }
}

// The strip under the cards: each item's sentence, once (2 guides both say "The guide downloads from
// here.", and the strip says it once: the product-types review, 13 Oct)
export function infoText(items) {
  return [...new Set((items || []).map(infoLine).filter(Boolean))].join(' ')
}

export function freeItems(d) {
  return (d.products || [])
    .filter(p => p.access_kind === 'free' && p.state === 'live' && (p.type === 'linked' || p.type === 'resource_pack'))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(p => {
      const card = p.settings?.card || {}
      return {
        id: p.id,
        name: p.name,
        // its own cover photo, when 1 is saved: it fills the top of the card in place of the art (3 Nov)
        coverPath: p.cover_path || null,
        art: artKey(p),
        artLine: card.art_line || '',
        artHeadline: card.art_headline || '',
        kindLabel: card.kind_label || '',
        blurb: card.blurb || '',
        infoLine: card.info_line || '',
        action: actionFor(p, d),
      }
    })
}

// The Free webinars product, unless it's held back from them (then they can't read its row)
export function webinarsProduct(d) {
  return (d.products || []).find(p => p.type === 'webinar' && p.access_kind === 'free' && p.state === 'live') || null
}

// The line under "You're in" in the joined box. The challenge's emails are Adam's MailerLite
// automation, and n8n answers every join the same way, so the hub claims nothing about delivery or
// which day comes next (Adam, 5 Oct): just after joining, in this visit, nothing more than
// "You're in" (null here); on a later visit, or when restart-join says they'd joined before, when
// they joined, "You joined on Mon 28 Sep.".
export function joinedLine({ joinedAt, justNow = false }) {
  if (justNow) return null
  return `You joined on ${f.shortDate(joinedAt)}.`
}
